import { FC, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as ModalCloseIcon } from 'src/assets/svg/modal-close.svg'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import styles from './pasteLinksModal.module.scss'
import { NameLocationsModal } from '../NameLocationsModal'

interface PasteLinksModalProps {
  isOpen: boolean
  onBack: () => void
  onClose: () => void
  onContinue: (links: string) => void
}

export const PasteLinksModal: FC<PasteLinksModalProps> = ({
  isOpen,
  onBack,
  onClose,
  onContinue,
}) => {
  const [links, setLinks] = useState('')
  const [isNamingOpen, setIsNamingOpen] = useState(false)

  useDisableScroll(isOpen)

  if (!isOpen) return null

  const hasContent = links.trim().length > 0

  const parsedUrls = links
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

  const handleClose = () => {
    setLinks('')
    setIsNamingOpen(false)
    onClose()
  }

  const handleContinue = () => {
    if (!hasContent) return
    setIsNamingOpen(true)
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  return (
    <>
      {!isNamingOpen &&
        createPortal(
          <div
            className={styles.overlay}
            onClick={handleOverlayClick}
            role="presentation"
          >
            <div className={styles.modal}>
              {/* Header */}
              <div className={styles.header}>
                <h2 className={styles.title}>Paste links</h2>
                <button
                  className={styles.closeBtn}
                  onClick={handleClose}
                  type="button"
                >
                  <ModalCloseIcon />
                </button>
              </div>

              {/* Step progress */}
              <div className={styles.steps}>
                <div className={`${styles.step} ${styles.stepActive}`} />
                <div className={styles.step} />
              </div>

              {/* Body */}
              <div className={styles.body}>
                <p className={styles.subtitle}>
                  Paste one or more location links.
                </p>
                <textarea
                  className={styles.textarea}
                  onChange={(e) => setLinks(e.target.value)}
                  placeholder={
                    'https://example.com/google-123\nhttps://example.com/google-1234\nhttps://example.com/google-123456'
                  }
                  value={links}
                />
              </div>

              {/* Footer */}
              <div className={styles.footer}>
                <button
                  className={styles.backBtn}
                  onClick={onBack}
                  type="button"
                >
                  Back
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={handleClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={styles.continueBtn}
                  disabled={!hasContent}
                  onClick={handleContinue}
                  type="button"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <NameLocationsModal
        isOpen={isNamingOpen}
        onBack={() => setIsNamingOpen(false)}
        onClose={handleClose}
        onSave={() => {
          onContinue(links)
          handleClose()
        }}
        urls={parsedUrls}
      />
    </>
  )
}
